'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

type AuthMode = 'signin' | 'register' | 'magic';

interface SignInInputs {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface RegisterInputs {
  fullName: string;
  email: string;
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
}

interface MagicLinkInputs {
  email: string;
}

interface DemoCredential {
  role: string;
  email: string;
  password: string;
  description: string;
}

const demoCredentials: DemoCredential[] = [
  {
    role: 'Account Manager',
    email: 'sarah.johnson@guardian-demo.com',
    password: 'Guardian2026!',
    description: "Adult child managing parent's profile",
  },
  {
    role: 'Wearer',
    email: 'margaret.thompson@guardian-demo.com',
    password: 'SafeWatch2026!',
    description: 'At-risk person managing own profile',
  },
];

export default function AuthForm() {
  const [mode, setMode] = useState<AuthMode>('signin');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [authError, setAuthError] = useState('');
  const router = useRouter();
  const { signIn, signUp, signInWithMagicLink, signInWithGoogle } = useAuth();

  const signInForm = useForm<SignInInputs>({
    defaultValues: { email: '', password: '', rememberMe: false },
  });

  const registerForm = useForm<RegisterInputs>({
    defaultValues: { fullName: '', email: '', password: '', confirmPassword: '', agreeToTerms: false },
  });

  const magicForm = useForm<MagicLinkInputs>({
    defaultValues: { email: '' },
  });

  const checkOnboardingAndRedirect = async () => {
    const { createClient } = await import('@/lib/supabase/client');
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { router.push('/sign-up-login-screen'); return; }

    const { data: onboarding } = await supabase
      .from('user_onboarding')
      .select('step_essentials_done')
      .eq('user_id', user.id)
      .single();

    if (!onboarding || !onboarding.step_essentials_done) {
      router.push('/onboarding');
    } else {
      router.push('/account-dashboard');
    }
  };

  const handleSignIn = signInForm.handleSubmit(async (data) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);
      toast.success('Signed in', { description: 'Checking your profile…' });
      await checkOnboardingAndRedirect();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Invalid credentials';
      signInForm.setError('email', { message });
    } finally {
      setIsLoading(false);
    }
  });

  const handleRegister = registerForm.handleSubmit(async (data) => {
    if (data.password !== data.confirmPassword) {
      registerForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, data.fullName);
      toast.success('Account created', { description: 'Setting up your Guardian profile.' });
      router.push('/onboarding');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      registerForm.setError('email', { message });
    } finally {
      setIsLoading(false);
    }
  });

  const handleMagicLink = magicForm.handleSubmit(async (data) => {
    setIsLoading(true);
    try {
      await signInWithMagicLink(data.email);
      setMagicLinkSent(true);
      toast.success('Magic link sent', { description: 'Check your email inbox.' });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to send magic link';
      magicForm.setError('email', { message });
    } finally {
      setIsLoading(false);
    }
  });

  const handleSocialSignIn = useCallback(async (provider: 'google') => {
    setAuthError('');
    // Google refuses to render inside iframes (e.g. an editor preview), so run the
    // whole flow in a top-level tab where the sign-in cookies are also first-party.
    if (window.self !== window.top) {
      window.open(`${window.location.origin}/sign-up-login-screen?oauth=${provider}`, '_blank', 'noopener');
      toast.info('Google sign-in opened in a new tab.');
      return;
    }
    setIsLoading(true);
    try {
      if (provider === 'google') {
        await signInWithGoogle();
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Sign-in failed';
      setAuthError(message);
      toast.error(message);
      setIsLoading(false);
    }
  }, [signInWithGoogle]);

  // Surface errors returned by /auth/callback and resume a sign-in started from an iframe.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('auth_error');
    const oauth = params.get('oauth');
    if (!error && !oauth) return;

    params.delete('auth_error');
    params.delete('oauth');
    const query = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);

    if (error) {
      setAuthError(error);
      toast.error('Sign-in failed', { description: error });
    } else if (oauth === 'google') {
      handleSocialSignIn('google');
    }
  }, [handleSocialSignIn]);

  const autofillCredentials = (cred: DemoCredential) => {
    signInForm.setValue('email', cred.email);
    signInForm.setValue('password', cred.password);
    signInForm.clearErrors();
    setMode('signin');
    toast.info(`Demo credentials loaded — ${cred.role}`, { duration: 2000 });
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel — clinical branding */}
      <div className="hidden lg:flex lg:w-[48%] xl:w-[50%] bg-primary flex-col justify-between p-10 xl:p-14 relative overflow-hidden">
        {/* Subtle structural lines */}
        <div className="absolute inset-0 opacity-[0.04]">
          <div className="absolute top-0 left-1/3 w-px h-full bg-white" />
          <div className="absolute top-0 left-2/3 w-px h-full bg-white" />
          <div className="absolute top-1/3 left-0 w-full h-px bg-white" />
          <div className="absolute top-2/3 left-0 w-full h-px bg-white" />
        </div>

        {/* Logo */}
        <div className="flex items-center gap-3 relative z-10">
          <AppLogo size={36} />
          <span className="text-white font-bold text-lg tracking-tight">Guardian</span>
        </div>

        {/* Core copy */}
        <div className="relative z-10 space-y-10">
          <div>
            <h1 className="text-3xl xl:text-4xl font-bold text-white leading-snug mb-4">
              Continuous safety monitoring<br />
              for people who live alone.
            </h1>
            <p className="text-slate-400 text-base leading-relaxed max-w-sm">
              Guardian pairs with a smartwatch to detect falls and abnormal vital patterns, then contacts your family circle — in order — until someone responds.
            </p>
          </div>

          {/* Feature list — plain, functional */}
          <div className="space-y-4 border-l-2 border-slate-700 pl-5">
            {[
              { label: 'Fall detection', detail: 'Accelerometer-based, under 30 seconds' },
              { label: 'Staged alerts', detail: 'Person first, then family, then professional responders' },
              { label: 'Location sharing', detail: 'GPS coordinates sent with every alert' },
              { label: 'Medical summary', detail: 'Blood type, medications, allergies — delivered at the moment of need' },
            ].map((item) => (
              <div key={`feature-${item.label}`}>
                <p className="text-white text-sm font-semibold">{item.label}</p>
                <p className="text-slate-500 text-xs mt-0.5">{item.detail}</p>
              </div>
            ))}
          </div>

          {/* Families count — no hype */}
          <div className="flex items-center gap-3 pt-2">
            <div className="flex -space-x-2">
              {['ST', 'MC', 'PW', 'RK'].map((initials) => (
                <div
                  key={`avatar-${initials}`}
                  className="w-8 h-8 rounded-full bg-slate-700 border-2 border-primary flex items-center justify-center text-slate-300 text-xs font-bold"
                >
                  {initials}
                </div>
              ))}
            </div>
            <p className="text-slate-400 text-sm">Used by 12,400 families</p>
          </div>
        </div>

        <p className="text-slate-700 text-xs relative z-10">
          © 2026 Guardian Safety Technologies. All rights reserved.
        </p>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-10 overflow-y-auto bg-background">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2 mb-8">
          <AppLogo size={32} />
          <span className="font-bold text-lg text-foreground">Guardian</span>
        </div>

        <div className="w-full max-w-md">
          {/* Mode tabs */}
          <div className="flex bg-muted rounded p-1 mb-8 border border-border">
            <button
              onClick={() => setMode('signin')}
              className={`flex-1 py-2 text-sm font-semibold rounded transition-all duration-200 ${
                mode === 'signin' ? 'bg-card text-foreground shadow-sm border border-border' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => setMode('register')}
              className={`flex-1 py-2 text-sm font-semibold rounded transition-all duration-200 ${
                mode === 'register' ? 'bg-card text-foreground shadow-sm border border-border' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Create Account
            </button>
            <button
              onClick={() => { setMode('magic'); setMagicLinkSent(false); }}
              className={`flex-1 py-2 text-sm font-semibold rounded transition-all duration-200 ${
                mode === 'magic' ? 'bg-card text-foreground shadow-sm border border-border' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Magic Link
            </button>
          </div>

          {authError && (
            <div role="alert" className="mb-6 px-3 py-2.5 rounded border border-red-200 bg-red-50 text-sm text-red-700">
              <p className="font-semibold">Sign-in failed</p>
              <p className="text-xs mt-0.5 break-words">{authError}</p>
            </div>
          )}

          {/* Social sign-in (not shown on magic link tab) */}
          {mode !== 'magic' && (
            <>
              <div className="space-y-2.5 mb-6">
                <button
                  type="button"
                  onClick={() => handleSocialSignIn('google')}
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-3 py-2.5 px-4 rounded border border-border bg-card hover:bg-muted transition-colors duration-150 text-sm font-medium text-foreground disabled:opacity-50"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  Continue with Google
                </button>
              </div>
              <div className="flex items-center gap-3 mb-6">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground">or use email</span>
                <div className="flex-1 h-px bg-border" />
              </div>
            </>
          )}

          {/* Sign In form */}
          {mode === 'signin' && (
            <div className="slide-up">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-foreground">Sign in to Guardian</h2>
                <p className="text-muted-foreground text-sm mt-1">Enter your credentials to access your account.</p>
              </div>
              <form onSubmit={handleSignIn} className="space-y-4">
                <div>
                  <label htmlFor="signin-email" className="form-label">Email address</label>
                  <input
                    id="signin-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    className={`form-input ${signInForm.formState.errors.email ? 'border-emergency ring-1 ring-emergency' : ''}`}
                    {...signInForm.register('email', {
                      required: 'Email is required',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                    })}
                  />
                  {signInForm.formState.errors.email && (
                    <p className="form-error"><Icon name="ExclamationCircleIcon" size={13} />{signInForm.formState.errors.email.message}</p>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label htmlFor="signin-password" className="form-label mb-0">Password</label>
                    <button type="button" onClick={() => { setMode('magic'); setMagicLinkSent(false); }} className="text-xs text-accent hover:underline font-medium">
                      Sign in without password →
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      id="signin-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="••••••••"
                      className={`form-input pr-10 ${signInForm.formState.errors.password ? 'border-emergency ring-1 ring-emergency' : ''}`}
                      {...signInForm.register('password', {
                        required: 'Password is required',
                        minLength: { value: 8, message: 'Password must be at least 8 characters' },
                      })}
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                    </button>
                  </div>
                  {signInForm.formState.errors.password && (
                    <p className="form-error"><Icon name="ExclamationCircleIcon" size={13} />{signInForm.formState.errors.password.message}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input id="remember-me" type="checkbox" className="w-4 h-4 rounded border-input accent-accent" {...signInForm.register('rememberMe')} />
                  <label htmlFor="remember-me" className="text-sm text-muted-foreground cursor-pointer">Keep me signed in for 30 days</label>
                </div>
                <button type="submit" disabled={isLoading} className="btn-primary w-full py-3 text-sm mt-2">
                  {isLoading ? <><Icon name="ArrowPathIcon" size={16} className="animate-spin" />Signing in…</> : <><Icon name="ShieldCheckIcon" size={16} />Sign In</>}
                </button>
              </form>
              <DemoCredentialsBox credentials={demoCredentials} onAutofill={autofillCredentials} />
            </div>
          )}

          {/* Register form */}
          {mode === 'register' && (
            <div className="slide-up">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-foreground">Create your account</h2>
                <p className="text-muted-foreground text-sm mt-1">Set up Guardian for yourself or someone in your care.</p>
              </div>
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <label htmlFor="reg-name" className="form-label">Full name</label>
                  <input id="reg-name" type="text" autoComplete="name" placeholder="Margaret Thompson"
                    className={`form-input ${registerForm.formState.errors.fullName ? 'border-emergency ring-1 ring-emergency' : ''}`}
                    {...registerForm.register('fullName', { required: 'Full name is required', minLength: { value: 2, message: 'Name must be at least 2 characters' } })}
                  />
                  {registerForm.formState.errors.fullName && <p className="form-error"><Icon name="ExclamationCircleIcon" size={13} />{registerForm.formState.errors.fullName.message}</p>}
                </div>
                <div>
                  <label htmlFor="reg-email" className="form-label">Email address</label>
                  <input id="reg-email" type="email" autoComplete="email" placeholder="you@example.com"
                    className={`form-input ${registerForm.formState.errors.email ? 'border-emergency ring-1 ring-emergency' : ''}`}
                    {...registerForm.register('email', { required: 'Email is required', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' } })}
                  />
                  {registerForm.formState.errors.email && <p className="form-error"><Icon name="ExclamationCircleIcon" size={13} />{registerForm.formState.errors.email.message}</p>}
                </div>
                <div>
                  <label htmlFor="reg-password" className="form-label">Password</label>
                  <p className="text-xs text-muted-foreground mb-1.5">At least 8 characters, including one uppercase letter and one number</p>
                  <div className="relative">
                    <input id="reg-password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" placeholder="Create a strong password"
                      className={`form-input pr-10 ${registerForm.formState.errors.password ? 'border-emergency ring-1 ring-emergency' : ''}`}
                      {...registerForm.register('password', { required: 'Password is required', minLength: { value: 8, message: 'Password must be at least 8 characters' }, pattern: { value: /^(?=.*[A-Z])(?=.*\d)/, message: 'Must include at least one uppercase letter and one number' } })}
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                    </button>
                  </div>
                  {registerForm.formState.errors.password && <p className="form-error"><Icon name="ExclamationCircleIcon" size={13} />{registerForm.formState.errors.password.message}</p>}
                </div>
                <div>
                  <label htmlFor="reg-confirm" className="form-label">Confirm password</label>
                  <div className="relative">
                    <input id="reg-confirm" type={showConfirmPassword ? 'text' : 'password'} autoComplete="new-password" placeholder="Repeat your password"
                      className={`form-input pr-10 ${registerForm.formState.errors.confirmPassword ? 'border-emergency ring-1 ring-emergency' : ''}`}
                      {...registerForm.register('confirmPassword', { required: 'Please confirm your password' })}
                    />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}>
                      <Icon name={showConfirmPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
                    </button>
                  </div>
                  {registerForm.formState.errors.confirmPassword && <p className="form-error"><Icon name="ExclamationCircleIcon" size={13} />{registerForm.formState.errors.confirmPassword.message}</p>}
                </div>
                <div>
                  <div className="flex items-start gap-2">
                    <input id="agree-terms" type="checkbox" className="w-4 h-4 mt-0.5 rounded border-input accent-accent flex-shrink-0"
                      {...registerForm.register('agreeToTerms', { required: 'You must agree to the terms to continue' })}
                    />
                    <label htmlFor="agree-terms" className="text-sm text-muted-foreground cursor-pointer leading-relaxed">
                      I agree to Guardian&apos;s <span className="text-accent hover:underline cursor-pointer">Terms of Service</span> and <span className="text-accent hover:underline cursor-pointer">Privacy Policy</span>
                    </label>
                  </div>
                  {registerForm.formState.errors.agreeToTerms && <p className="form-error mt-1"><Icon name="ExclamationCircleIcon" size={13} />{registerForm.formState.errors.agreeToTerms.message}</p>}
                </div>
                <button type="submit" disabled={isLoading} className="btn-primary w-full py-3 text-sm mt-2">
                  {isLoading ? <><Icon name="ArrowPathIcon" size={16} className="animate-spin" />Creating account…</> : <><Icon name="ShieldCheckIcon" size={16} />Create Account</>}
                </button>
              </form>
              <p className="text-center text-xs text-muted-foreground mt-6">
                Already have an account?{' '}
                <button onClick={() => setMode('signin')} className="text-accent font-semibold hover:underline">Sign in</button>
              </p>
            </div>
          )}

          {/* Magic link form */}
          {mode === 'magic' && (
            <div className="slide-up">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-foreground">Sign in with a magic link</h2>
                <p className="text-muted-foreground text-sm mt-1">
                  Enter your email and we will send you a one-click sign-in link. No password needed.
                </p>
              </div>

              {magicLinkSent ? (
                <div className="rounded-lg border border-green-200 bg-green-50 p-5 text-center">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p className="text-sm font-semibold text-green-800 mb-1">Check your inbox</p>
                  <p className="text-xs text-green-700">
                    We sent a sign-in link to <strong>{magicForm.getValues('email')}</strong>. Click the link in the email to sign in. The link expires in 1 hour.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setMagicLinkSent(false); magicForm.reset(); }}
                    className="mt-4 text-xs text-green-700 hover:underline"
                  >
                    Use a different email
                  </button>
                </div>
              ) : (
                <form onSubmit={handleMagicLink} className="space-y-4">
                  <div>
                    <label htmlFor="magic-email" className="form-label">Email address</label>
                    <input
                      id="magic-email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      className={`form-input ${magicForm.formState.errors.email ? 'border-emergency ring-1 ring-emergency' : ''}`}
                      {...magicForm.register('email', {
                        required: 'Email is required',
                        pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                      })}
                    />
                    {magicForm.formState.errors.email && (
                      <p className="form-error"><Icon name="ExclamationCircleIcon" size={13} />{magicForm.formState.errors.email.message}</p>
                    )}
                  </div>
                  <button type="submit" disabled={isLoading} className="btn-primary w-full py-3 text-sm">
                    {isLoading ? <><Icon name="ArrowPathIcon" size={16} className="animate-spin" />Sending…</> : <>Send magic link</>}
                  </button>
                  <p className="text-center text-xs text-muted-foreground">
                    Prefer a password?{' '}
                    <button type="button" onClick={() => setMode('signin')} className="text-accent font-semibold hover:underline">Sign in with password</button>
                  </p>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DemoCredentialsBox({
  credentials,
  onAutofill,
}: {
  credentials: DemoCredential[];
  onAutofill: (cred: DemoCredential) => void;
}) {
  return (
    <div className="mt-6 rounded border border-border bg-secondary p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon name="InformationCircleIcon" size={15} className="text-muted-foreground flex-shrink-0" />
        <p className="text-xs font-semibold text-muted-foreground">Demo accounts — click to load credentials</p>
      </div>
      <div className="space-y-2">
        {credentials.map((cred) => (
          <button
            key={`demo-${cred.role}`}
            type="button"
            onClick={() => onAutofill(cred)}
            className="w-full text-left px-3 py-2.5 rounded border border-border bg-card hover:border-accent hover:bg-muted transition-all duration-150"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-foreground">{cred.role}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{cred.description}</p>
                <p className="text-xs text-accent font-mono mt-1">{cred.email}</p>
              </div>
              <Icon name="ChevronRightIcon" size={14} className="text-muted-foreground" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}